import type { EstadoDeCobro, MarcaDelCobro, MotivoRechazo } from "./estadoDeCobro";

/**
 * Los textos del aviso de cobro rechazado (ADR-2026-10-26-cobros-rechazados-en-la-plataforma). Un
 * solo lugar para la home, el header mobile, /subscription y el modal de la sidebar: el pedido es
 * que digan exactamente lo mismo en todos lados. Tono formal, con voseo.
 */

const fechaLarga = (d: Date) => d.toLocaleDateString("es-AR", { day: "numeric", month: "long" });

const mayuscula = (t: string) => `${t.charAt(0).toUpperCase()}${t.slice(1)}`;

function laTarjeta(tarjeta: string | null): string {
  return tarjeta ? `tu tarjeta terminada en ${tarjeta}` : "tu tarjeta";
}

/** "Tu tarjeta terminada en 1234 no tiene fondos suficientes." — null si no hay nada que decir. */
export function oracionDelMotivo(motivo: MotivoRechazo | null, tarjeta: string | null): string | null {
  switch (motivo) {
    case "fondos":
      return `${mayuscula(laTarjeta(tarjeta))} no tiene fondos suficientes.`;
    case "tarjeta_vencida":
      return `${mayuscula(laTarjeta(tarjeta))} está vencida.`;
    case "banco":
      return `El banco rechazó el pago con ${laTarjeta(tarjeta)}.`;
    default:
      return tarjeta ? `El cobro con tu tarjeta terminada en ${tarjeta} no fue aprobado.` : null;
  }
}

/** "Marca A", "Marca A y Marca B", "Marca A, Marca B y Marca C". */
function enumerar(nombres: string[]): string {
  if (nombres.length <= 1) return nombres[0] ?? "";
  return `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
}

/**
 * El aviso del plan de una MARCA (facturación por marca, ADR-2026-10-29-cobro-rechazado-por-marca).
 * Mismas piezas que el de la empresa, pero nombra la marca y le habla al miembro, que no puede
 * cambiar la tarjeta.
 */
function cuerpoDeCobroDeMarca(estado: EstadoDeCobro & { tipo: "rechazo" | "baja_programada" }, marca: MarcaDelCobro): string {
  const partes: (string | null)[] = [];
  if (estado.tipo === "rechazo") {
    partes.push(
      oracionDelMotivo(estado.motivo, estado.tarjeta),
      estado.proximoReintento
        ? `El cobro se volverá a intentar el ${fechaLarga(estado.proximoReintento)}.`
        : "El cobro se volverá a intentar en los próximos días.",
      "Mientras tanto la marca sigue usando su plan.",
      marca.esDueno ? null : "Pedile al admin de la cuenta que cambie la tarjeta.",
    );
  } else {
    partes.push(
      "El cobro del plan de esta marca fue rechazado y no se pudo completar en los reintentos.",
      oracionDelMotivo(estado.motivo, estado.tarjeta),
      marca.esDueno
        ? "Para conservarlo, cambiá la tarjeta antes de esa fecha."
        : "Para conservarlo, pedile al admin de la cuenta que cambie la tarjeta antes de esa fecha.",
    );
  }
  if (marca.otras.length > 0) partes.push(`También se rechazó el cobro de ${enumerar(marca.otras)}.`);
  return partes.filter(Boolean).join(" ");
}

export function tituloDeCobro(estado: EstadoDeCobro): string {
  if (estado.tipo !== "dado_de_baja" && estado.marca) {
    return estado.tipo === "rechazo"
      ? `El cobro del plan de ${estado.marca.nombre} fue rechazado`
      : `El plan de ${estado.marca.nombre} se dará de baja el ${fechaLarga(estado.fechaBaja)}`;
  }
  switch (estado.tipo) {
    case "rechazo":
      return "El cobro de tu suscripción fue rechazado";
    case "baja_programada":
      return `Tu plan se dará de baja el ${fechaLarga(estado.fechaBaja)}`;
    case "dado_de_baja":
      return "Tu plan fue dado de baja";
  }
}

export function cuerpoDeCobro(estado: EstadoDeCobro): string {
  if (estado.tipo !== "dado_de_baja" && estado.marca) return cuerpoDeCobroDeMarca(estado, estado.marca);
  const partes: (string | null)[] = [];
  switch (estado.tipo) {
    case "rechazo":
      partes.push(
        oracionDelMotivo(estado.motivo, estado.tarjeta),
        estado.proximoReintento
          ? `El cobro se volverá a intentar el ${fechaLarga(estado.proximoReintento)}.`
          : "El cobro se volverá a intentar en los próximos días.",
        "Mientras tanto podés continuar utilizando tu plan.",
      );
      break;
    case "baja_programada":
      if (estado.plataforma === "mp") {
        partes.push(
          "El cobro de tu suscripción fue rechazado.",
          oracionDelMotivo(estado.motivo, estado.tarjeta),
          "MercadoPago seguirá intentando el cobro. Si cambiás la tarjeta en tu cuenta de MercadoPago, el próximo intento se hará con la nueva.",
        );
      } else {
        partes.push(
          "El cobro de tu suscripción fue rechazado y no se pudo completar en los reintentos.",
          oracionDelMotivo(estado.motivo, estado.tarjeta),
          "Para conservar tu plan, regularizá el pago antes de esa fecha.",
        );
      }
      break;
    case "dado_de_baja":
      partes.push(
        "No pudimos cobrar la renovación de tu suscripción.",
        "Podés volver a suscribirte cuando quieras.",
      );
      break;
  }
  return partes.filter(Boolean).join(" ");
}

/** El botón de la sidebar que reemplaza a "Ver planes". */
export function etiquetaDeCobro(estado: EstadoDeCobro): string {
  return estado.tipo === "dado_de_baja" ? "Plan dado de baja" : "Pago rechazado";
}
